import { forwardRef, useEffect, useId, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cx } from './cx';

export function Optional() {
  return <span className="font-normal text-muted"> (optional)</span>;
}

export function FieldError({ id, children }: { id?: string; children?: ReactNode }) {
  if (!children) return null;
  return <div id={id} className="mt-1 text-[13px] font-semibold text-bad">{children}</div>;
}

type Common = { label: ReactNode; optional?: boolean; hint?: ReactNode; error?: string | null; small?: boolean; className?: string };

export function TextField({ label, optional, hint, error, small, className, id, ...rest }: Common & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fid = id ?? auto;
  const errId = `${fid}-err`;
  const hintId = `${fid}-hint`;
  return (
    <div className={className}>
      <label htmlFor={fid} className={cx('block', small ? 'mb-1 text-[13px] font-semibold text-ink-2' : 'mb-1.5 text-sm font-semibold')}>
        {label}{optional && <Optional />}
      </label>
      {hint && <div id={hintId} className="-mt-1 mb-1.5 text-[13px] text-muted">{hint}</div>}
      <input
        id={fid}
        className={cx('input', error && 'input-invalid')}
        aria-invalid={error ? true : undefined}
        aria-describedby={cx(error && errId, hint ? hintId : false) || undefined}
        {...rest}
      />
      <FieldError id={errId}>{error}</FieldError>
    </div>
  );
}

/** Password input with a show/hide toggle. Hidden again whenever the field is cleared. */
export function PasswordField({ label, optional, hint, error, small, className, id, value, ...rest }: Common & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const auto = useId();
  const fid = id ?? auto;
  const errId = `${fid}-err`;
  const hintId = `${fid}-hint`;
  const [visible, setVisible] = useState(false);
  useEffect(() => { if (value === '') setVisible(false); }, [value]);
  return (
    <div className={className}>
      <label htmlFor={fid} className={cx('block', small ? 'mb-1 text-[13px] font-semibold text-ink-2' : 'mb-1.5 text-sm font-semibold')}>
        {label}{optional && <Optional />}
      </label>
      {hint && <div id={hintId} className="-mt-1 mb-1.5 text-[13px] text-muted">{hint}</div>}
      <div className="relative">
        <input
          id={fid}
          type={visible ? 'text' : 'password'}
          className={cx('input pr-12', error && 'input-invalid')}
          aria-invalid={error ? true : undefined}
          aria-describedby={cx(error && errId, hint ? hintId : false) || undefined}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          aria-controls={fid}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-[10px] text-muted hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand"
        >
          {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
        </button>
      </div>
      <FieldError id={errId}>{error}</FieldError>
    </div>
  );
}

export const TextArea = forwardRef<HTMLTextAreaElement, Common & TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ label, optional, hint, error, small, className, id, rows = 2, ...rest }, ref) {
  const auto = useId();
  const fid = id ?? auto;
  const hintId = `${fid}-hint`;
  return (
    <div className={className}>
      <label htmlFor={fid} className={cx('block font-semibold', small ? 'mb-1 text-[13px] text-ink-2' : 'text-sm')}>
        {label}{optional && <Optional />}
      </label>
      {hint ? <div id={hintId} className="mb-1.5 mt-0.5 text-[13px] text-muted">{hint}</div> : !small && <div className="h-1.5" />}
      <textarea
        ref={ref}
        id={fid}
        rows={rows}
        aria-describedby={hint ? hintId : undefined}
        className={cx('textarea box-border w-full resize-y rounded-[10px] border border-line-strong bg-white px-3 py-2.5', small ? 'text-sm' : 'text-[15px]', error && 'input-invalid')}
        {...rest}
      />
      <FieldError>{error}</FieldError>
    </div>
  );
});

/** Number input that reports undefined when blank; keeps its own text so it can be cleared and retyped. */
export function NumberField({ value, onValue, ...rest }: Omit<Common & InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & { value: number | null | undefined; onValue: (n: number | undefined) => void }) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => {
    setText((t) => {
      const parsed = t.trim() === '' ? undefined : Math.max(0, Math.floor(Number(t)));
      if (parsed === value || (parsed === 0 && value === undefined)) return t;
      return value == null ? '' : String(value);
    });
  }, [value]);
  return (
    <TextField
      type="number"
      inputMode="numeric"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const raw = e.target.value.trim();
        const n = raw === '' ? undefined : Math.max(0, Math.floor(Number(raw)));
        onValue(n === undefined || Number.isNaN(n) ? undefined : n);
      }}
      {...rest}
    />
  );
}
